/**
 * App-wide plain-English glossary (W2-22, 2026-06-11).
 *
 * AUTHORING RULES (the contract — see docs/mockups/2026-06-11-w2-22-glossary-tooltips.html):
 * 1. Abbreviations spell themselves out FIRST via `expansion` — the tooltip
 *    title renders "QBI — Qualified Business Income". Omit for plain words.
 * 2. `plainEnglish` is ONE breath (≤280 chars), novice-first, answering
 *    "what does this mean for ME" — no unexplained sibling jargon inside it.
 * 3. `technical` is the 1-2 line anchor: IRC/section + key 2026 parameters.
 * 4. ACCURACY GUARDRAIL: every dollar value MUST match the codebase's verified
 *    constants (shared-layer tax-optimizer.js + src/lib/thresholds.ts, both
 *    pinned per Rev. Proc. 2025-32 / OBBBA P.L. 119-21 / SSA 2026 COLA).
 *    A wrong simplification is worse than none — when a term is risky to
 *    simplify, keep the plain line modest and precise. The repo-wide
 *    tax-constants drift guard scans this file; update values annually.
 * 5. ALIASES: multi-word ONLY for ambiguous words. Never bare "nexus"
 *    (product name!) — use "state nexus"/"multi-state nexus". Never bare
 *    "distribution" or "accrual" — use "owner distribution", "accrual
 *    accounting". Aliases + terms must be globally unique across entries.
 * 6. NEW JARGON ⇒ NEW ENTRY. Any PR that puts a new tax/compliance/accounting
 *    term, IRS form, IRC section, or abbreviation in front of users MUST add
 *    its glossary entry in the same PR. The completeness lint makes unknown
 *    keys a test failure, not a silent fallback.
 * 7. URLs: authoritative only — irs.gov, law.cornell.edu, or *.gov.
 */

export interface GlossaryEntry {
  /** stable kebab-case id, e.g. 'qbi' */
  key: string;
  /** canonical display term as it appears in UI, e.g. 'QBI' */
  term: string;
  /** additional matchable strings. MULTI-WORD ONLY for ambiguous words (never bare 'nexus'; 'owner distribution', 'accrual accounting'). */
  aliases?: string[];
  /** spelled-out form for abbreviations, e.g. 'Qualified Business Income' — shown as the tooltip title "QBI — Qualified Business Income". Omit for non-abbreviations. */
  expansion?: string;
  /** ≤280 chars. Novice-first. Answers "what does this mean for me" in one breath. NO unexplained jargon inside it. */
  plainEnglish: string;
  /** 1-2 lines: the IRC/section anchor + key 2026 parameters. */
  technical: string;
  /** e.g. 'IRC §199A' — omit if none */
  ircRef?: string;
  /** authoritative IRS.gov / Cornell LII URL — omit if none */
  url?: string;
  /** true for short all-caps abbreviations that must match CASE-SENSITIVELY (QBI, AMT, NIIT, FICA, FUTA, SALT, MAGI, AGI, SSTB, EBITDA, FMV, PTET, IRC, A/R, A/P — plus EIN, FIRE, NJ BAIT for the same reason). */
  caseSensitive?: boolean;
}

export const GLOSSARY: GlossaryEntry[] = [
  // --- Income & entities ---
  {
    key: 'additional-medicare',
    term: 'Additional Medicare Tax',
    aliases: ['Additional Medicare'],
    plainEnglish:
      'An extra 0.9% Medicare tax on wages or self-employment income above $200,000 single / $250,000 joint. Your employer doesn’t match this one — it comes entirely out of your pay.',
    technical:
      'IRC §3101(b)(2) · thresholds $200K single / $250K joint / $125K married filing separately, fixed since 2013 (not inflation-indexed) · reconciled on Form 8959.',
    ircRef: 'IRC §3101(b)(2)',
    url: 'https://www.irs.gov/forms-pubs/about-form-8959',
  },
  {
    key: 'agi',
    term: 'AGI',
    aliases: ['Adjusted Gross Income'],
    expansion: 'Adjusted Gross Income',
    plainEnglish:
      'Your total income minus a short list of adjustments (retirement contributions, half of self-employment tax, etc.) — before the standard or itemized deduction. Many limits and phase-outs key off this number.',
    technical:
      'IRC §62 · subtracting the standard or itemized deduction from AGI gives taxable income.',
    ircRef: 'IRC §62',
    url: 'https://www.irs.gov/e-file-providers/definition-of-adjusted-gross-income',
    caseSensitive: true,
  },
  {
    key: 'amt',
    term: 'AMT',
    aliases: ['Alternative Minimum Tax'],
    expansion: 'Alternative Minimum Tax',
    plainEnglish:
      'A parallel “minimum tax” calculation that removes certain deductions and makes you pay whichever is higher — regular tax or the minimum. Most owners never owe it, but very high deductions or exercising stock options can trigger it.',
    technical:
      'IRC §55 · 2026 exemption $90,100 single / $140,200 joint, phasing out above $500K / $1M at 50¢ per dollar (Rev. Proc. 2025-32 §3.10; OBBBA §70107).',
    ircRef: 'IRC §55',
    url: 'https://www.irs.gov/taxtopics/tc556',
    caseSensitive: true,
  },
  {
    key: 'c-corp',
    term: 'C-corp',
    aliases: ['C corporation', 'C-corporation'],
    expansion: 'C corporation',
    plainEnglish:
      'A regular corporation that pays its own income tax. Profit you take out as dividends is taxed AGAIN on your personal return — the famous “double tax.” In exchange: a flat 21% corporate rate and easier reinvestment or outside investment.',
    technical:
      'IRC §11 · 21% flat federal rate · files Form 1120 · no K-1s; owners receive W-2 wages and dividends.',
    ircRef: 'IRC §11',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/forming-a-corporation',
  },
  {
    key: 'effective-tax-rate',
    term: 'effective tax rate',
    aliases: ['marginal tax rate', 'effective rate', 'marginal rate'],
    plainEnglish:
      'Your marginal rate is the tax on your NEXT dollar (your bracket); your effective rate is the average across ALL your dollars — total tax ÷ total income. The effective rate is what you actually pay; it’s always lower than the bracket headline.',
    technical:
      'Marginal = top bracket rate applied to the last dollar earned · effective = total tax / total income.',
  },
  {
    key: 'ein',
    term: 'EIN',
    aliases: ['Employer Identification Number'],
    expansion: 'Employer Identification Number',
    plainEnglish:
      'Your business’s ID number with the IRS — like a Social Security number for the company. You need it to open a bank account, run payroll, and file business returns. Free to get directly from the IRS.',
    technical:
      'Issued online via IRS.gov · banks, payroll providers, and tax forms ask for it constantly.',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/get-an-employer-identification-number',
    caseSensitive: true,
  },
  {
    key: 'estimated-tax',
    term: 'estimated tax',
    aliases: ['estimated taxes', 'quarterly estimates', 'estimated payments'],
    plainEnglish:
      'Business income has no employer withholding, so the IRS expects you to pay as you go — four payments a year (April, June, September, January). Skip them and a penalty accrues even if you pay in full with your return.',
    technical:
      'IRC §6654 · due 4/15, 6/15, 9/15, 1/15 · paying the safe-harbor amount guarantees no underpayment penalty.',
    ircRef: 'IRC §6654',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/estimated-taxes',
  },
  {
    key: 'k-1',
    term: 'K-1',
    aliases: ['Schedule K-1'],
    expansion: 'Schedule K-1',
    plainEnglish:
      'If your business is an S-corp or partnership, the company itself doesn’t pay income tax — the profit lands on YOUR personal return. The K-1 is the slip reporting your share. You owe tax on it even if you left the cash in the business.',
    technical:
      'Form 1120-S / 1065 · pass-through allocation · C-corps don’t issue K-1s; owners get wages + dividends.',
    url: 'https://www.irs.gov/forms-pubs/about-schedule-k-1-form-1120-s',
  },
  {
    key: 'magi',
    term: 'MAGI',
    aliases: ['Modified Adjusted Gross Income', 'modified AGI'],
    expansion: 'Modified Adjusted Gross Income',
    plainEnglish:
      'Your adjusted gross income with a few deductions added back. The IRS uses it as the yardstick for phase-outs — whether you qualify for certain breaks (or owe certain surtaxes) depends on where this number lands.',
    technical:
      'Definition varies by provision — e.g. the 3.8% investment-income surtax (IRC §1411) and the state-tax deduction cap each use their own add-backs.',
    caseSensitive: true,
  },
  {
    key: 'niit',
    term: 'NIIT',
    aliases: ['Net Investment Income Tax', '§1411'],
    expansion: 'Net Investment Income Tax',
    plainEnglish:
      'An extra 3.8% tax on investment income (interest, dividends, capital gains) once your income passes $200,000 single / $250,000 joint. Profit from a business you actively run is generally exempt.',
    technical:
      'IRC §1411 · MAGI thresholds $200K / $250K ($125K married filing separately), not inflation-indexed · reported on Form 8960.',
    ircRef: 'IRC §1411',
    url: 'https://www.irs.gov/forms-pubs/about-form-8960',
    caseSensitive: true,
  },
  {
    key: 'owner-distribution',
    term: 'owner distribution',
    aliases: ['owner distributions', 'shareholder distribution', 'shareholder distributions', 'owner draw', '§1368'],
    plainEnglish:
      'Profit you take out of your S-corp on top of your salary. No payroll tax on it — that’s the S-corp advantage — but it isn’t extra free money at tax time: you already owe income tax on the profit whether or not you withdraw it.',
    technical:
      'IRC §1368 · tax-free up to your stock basis · must come AFTER reasonable W-2 compensation.',
    ircRef: 'IRC §1368',
    url: 'https://www.law.cornell.edu/uscode/text/26/1368',
  },
  {
    key: 'partnership',
    term: 'partnership',
    aliases: ['multi-member LLC'],
    plainEnglish:
      'Two or more owners sharing a business that doesn’t pay its own income tax — each partner’s share of profit flows to their personal return via a K-1, whether or not cash was actually paid out.',
    technical:
      'IRC Subchapter K · files Form 1065 (informational) · general partners typically owe self-employment tax on their share.',
    ircRef: 'IRC §701',
    url: 'https://www.irs.gov/businesses/partnerships',
  },
  {
    key: 'pass-through',
    term: 'pass-through',
    aliases: ['pass-through entity', 'passthrough', 'PTE'],
    plainEnglish:
      'A business whose profit isn’t taxed at the company level — it “passes through” to the owners’ personal returns (S-corps, partnerships, sole proprietorships). One layer of tax, and usually eligible for the 20% business-income deduction.',
    technical:
      'Contrast: C-corporations pay their own tax · pass-through profit may qualify for the IRC §199A deduction.',
    caseSensitive: true,
  },
  {
    key: 'qbi',
    term: 'QBI',
    aliases: ['Qualified Business Income', '§199A', 'Section 199A'],
    expansion: 'Qualified Business Income',
    plainEnglish:
      'A discount on your taxes for owning a small business: skip paying tax on up to 20% of your business profit. Phases out at higher incomes; C-corporations don’t get it.',
    technical:
      'IRC §199A · pass-through entities only · 2026 phase-out begins at $201,750 single / $403,500 joint, over a $75K / $150K range (Rev. Proc. 2025-32 §4.26; OBBBA §70105).',
    ircRef: 'IRC §199A',
    url: 'https://www.law.cornell.edu/uscode/text/26/199A',
    caseSensitive: true,
  },
  {
    key: 'reasonable-compensation',
    term: 'reasonable compensation',
    aliases: ['reasonable comp'],
    plainEnglish:
      'The pay an S-corp owner must take as a normal paycheck before taking the rest as profit. The IRS expects it to match what you’d pay someone else to do your job — paying yourself too little is the #1 audit trigger.',
    technical:
      'Rev. Rul. 74-44 · Watson v. United States (8th Cir. 2012) · S-corp shareholder-employees.',
    ircRef: 'Rev. Rul. 74-44 · IRC §162(a)',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/s-corporation-compensation-and-medical-insurance-issues',
  },
  {
    key: 's-corp',
    term: 'S-corp',
    aliases: ['S corporation', 'Subchapter S', 'S-corporation'],
    expansion: 'Subchapter S election',
    plainEnglish:
      'A tax status where the company’s profit “passes through” to your personal return instead of being taxed twice. You must pay yourself a reasonable salary first; the rest can come out as distributions.',
    technical:
      'IRC Subchapter S · elected via Form 2553 · profit reported on Schedule K-1.',
    ircRef: 'IRC §§1361–1379',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/s-corporations',
  },
  {
    key: 'safe-harbor',
    term: 'safe harbor',
    aliases: ['safe-harbor'],
    plainEnglish:
      'A “no-penalty guarantee”: if your estimates add up to at least last year’s tax, the IRS won’t fine you for underpaying — even if you owe more this year. (Over $150k, it’s 110%.)',
    technical: 'IRC §6654(d) · shortfall triggers the Form 2210 penalty.',
    ircRef: 'IRC §6654(d)',
    url: 'https://www.law.cornell.edu/uscode/text/26/6654',
  },
  {
    key: 'se-tax',
    term: 'SE tax',
    aliases: ['self-employment tax'],
    expansion: 'Self-Employment tax',
    plainEnglish:
      'The Social Security + Medicare taxes a self-employed person pays directly — both the employee AND employer halves, since you’re both. S-corp owners pay it only on their salary, not on profit distributions.',
    technical:
      'IRC §§1401–1403 · 15.3% on 92.35% of net self-employment income · Social Security portion caps at $184,500 of income for 2026 (SSA COLA) · Schedule SE.',
    ircRef: 'IRC §§1401–1403',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/self-employment-tax-social-security-and-medicare-taxes',
  },
  {
    key: 'sole-proprietorship',
    term: 'sole proprietorship',
    aliases: ['sole prop', 'sole proprietor', 'Schedule C'],
    plainEnglish:
      'The default for one person in business: you and the business are the same taxpayer. All profit lands on your personal return and ALL of it is hit with self-employment tax — there’s no salary/distribution split.',
    technical:
      'Reported on Schedule C (Form 1040) · profit subject to 15.3% self-employment tax · no separate business return.',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/sole-proprietorships',
  },
  {
    key: 'sstb',
    term: 'SSTB',
    aliases: ['Specified Service Trade or Business'],
    expansion: 'Specified Service Trade or Business',
    plainEnglish:
      'The IRS’s label for service businesses where the asset is the owner’s skill — medicine, law, accounting, consulting, financial advice. Above the income thresholds, these businesses lose the 20% business-income deduction entirely; others only see it limited.',
    technical:
      'IRC §199A(d)(2) · deduction phases out over $75K (single) / $150K (joint) above the 2026 thresholds of $201,750 / $403,500.',
    ircRef: 'IRC §199A(d)(2)',
    url: 'https://www.law.cornell.edu/uscode/text/26/199A',
    caseSensitive: true,
  },
  {
    key: 'taxable-income',
    term: 'taxable income',
    plainEnglish:
      'The number tax is actually computed on: your income after every deduction. Lower it through legitimate deductions and the tax brackets apply to less.',
    technical:
      'IRC §63 · adjusted gross income minus the standard deduction (or itemized deductions) and any qualified business-income deduction.',
    ircRef: 'IRC §63',
    url: 'https://www.law.cornell.edu/uscode/text/26/63',
  },
  {
    key: 'underpayment-penalty',
    term: 'underpayment penalty',
    plainEnglish:
      'An interest-style charge for paying too little tax during the year — through withholding or quarterly estimates. It accrues quarter by quarter; meeting a safe-harbor amount makes it go away entirely.',
    technical:
      'IRC §6654 · computed on Form 2210 at the federal interest rate, per quarter.',
    ircRef: 'IRC §6654',
    url: 'https://www.irs.gov/payments/underpayment-of-estimated-tax-by-individuals-penalty',
  },

  // --- Deductions ---
  {
    key: 'accountable-plan',
    term: 'accountable plan',
    plainEnglish:
      'A written policy that lets the company reimburse your business expenses (home office, mileage, phone) tax-free — no income to you, deductible to the business. Without one, reimbursements legally count as wages.',
    technical:
      'IRC §62(c) · requires a business connection, substantiation (receipts), and return of excess advances.',
    ircRef: 'IRC §62(c)',
    url: 'https://www.law.cornell.edu/uscode/text/26/62',
  },
  {
    key: 'bonus-depreciation',
    term: 'bonus depreciation',
    aliases: ['§168(k)', 'Section 168(k)'],
    plainEnglish:
      'An extra-fast write-off: deduct 100% of qualifying equipment the year it goes into service — no profit requirement, unlike its cousin §179. The asset must be IN SERVICE (set up and ready to use) by year-end, not just purchased.',
    technical:
      'IRC §168(k) · 100% rate restored for property acquired after Jan 19, 2025 (OBBBA §70301) · some states don’t conform and add it back.',
    ircRef: 'IRC §168(k)',
    url: 'https://www.law.cornell.edu/uscode/text/26/168',
  },
  {
    key: 'carryforward',
    term: 'carryforward',
    aliases: ['carry forward', 'carryover'],
    plainEnglish:
      'A deduction or credit you couldn’t fully use this year that rolls into next year instead of vanishing. Common with equipment write-offs in a loss year — the unused amount waits for a profitable one.',
    technical:
      'Rules vary by provision — e.g. unused §179 amounts carry forward indefinitely until business income can absorb them.',
  },
  {
    key: 'depreciation',
    term: 'depreciation',
    plainEnglish:
      'You deduct long-lasting purchases a piece at a time as they “wear out.” Bookkeeping and the tax return wear it out at different speeds — that’s why the two numbers differ on purpose.',
    technical:
      'MACRS (tax) vs straight-line (book) · Schedule M-1 reconciles the difference; expected, not an error.',
    ircRef: 'IRC §§167–168',
    url: 'https://www.irs.gov/publications/p946',
  },
  {
    key: 'employer-contribution',
    term: 'employer contribution',
    aliases: ['employer contributions', 'employer match', '§404', 'Section 404'],
    plainEnglish:
      'Money the business puts into your retirement plan on top of what you defer from pay. It’s a deductible business expense — one of the cleanest ways to move company profit into your own future, pre-tax.',
    technical:
      'IRC §404 · Solo 401(k): up to 20% of net self-employment income (25% of W-2 wages for S-corp owners), within the $72,000 combined cap for 2026.',
    ircRef: 'IRC §404',
    url: 'https://www.irs.gov/publications/p560',
  },
  {
    key: 'irc-162',
    term: 'IRC §162',
    aliases: ['§162(a)', 'Section 162', '§162', 'ordinary and necessary'],
    plainEnglish:
      '“Ordinary and necessary” is the master rule for business deductions: common in your line of work, and helpful to the business. Most everyday expenses — software, travel, insurance, professional fees — deduct under this one section.',
    technical:
      'IRC §162(a) · the default home for business expenses that don’t have their own special section.',
    ircRef: 'IRC §162(a)',
    url: 'https://www.law.cornell.edu/uscode/text/26/162',
  },
  {
    key: 'itemized-deduction',
    term: 'itemized deductions',
    aliases: ['itemized deduction'],
    plainEnglish:
      'Listing your actual deductible expenses (state taxes up to the cap, mortgage interest, charity, big medical bills) instead of taking the flat standard deduction. Worth it only when the total beats the standard amount.',
    technical:
      'IRC §63(d) · Schedule A · state/local taxes inside it are limited by the $40,400 SALT cap for 2026.',
    ircRef: 'IRC §63(d)',
    url: 'https://www.irs.gov/taxtopics/tc501',
  },
  {
    key: 'macrs',
    term: 'MACRS',
    expansion: 'Modified Accelerated Cost Recovery System',
    plainEnglish:
      'The tax rulebook for how fast equipment is written off — front-loaded, so you deduct more in the first years and less later. Each asset type gets an assigned life (computers 5 years, office furniture 7).',
    technical:
      'IRC §168 · IRS Pub 946 tables · the default method for tax depreciation; books typically use straight-line instead.',
    ircRef: 'IRC §168',
    url: 'https://www.irs.gov/publications/p946',
    caseSensitive: true,
  },
  {
    key: 'mileage-rate',
    term: 'standard mileage rate',
    aliases: ['mileage rate', 'IRS standard mileage rate'],
    plainEnglish:
      'Instead of tracking gas and repairs, deduct a flat amount per business mile — 72.5¢ for 2026. One rule: pick mileage OR actual vehicle costs per vehicle, not both.',
    technical:
      '72.5¢/mile for 2026 (IRS Notice 2026-10) · covers fuel, insurance, maintenance, and depreciation in one rate · log the date, miles, and business purpose for each trip.',
    url: 'https://www.irs.gov/tax-professionals/standard-mileage-rates',
  },
  {
    key: 'salt',
    term: 'SALT',
    aliases: ['SALT cap', 'state and local tax deduction'],
    expansion: 'State And Local Tax',
    plainEnglish:
      'Your state and local taxes (income + property) can be deducted on your federal return only up to a cap — $40,400 in 2026. Above roughly $500K of income the cap shrinks back toward $10,000.',
    technical:
      'IRC §164(b)(6) · 2026 cap $40,400 ($20,200 married filing separately) · MAGI phase-out above $500K reduces it 30¢ per $1 down to a $10,000 floor (OBBBA; Rev. Proc. 2025-32).',
    ircRef: 'IRC §164(b)(6)',
    url: 'https://www.law.cornell.edu/uscode/text/26/164',
    caseSensitive: true,
  },
  {
    key: 'section-105',
    term: 'Section 105',
    aliases: ['§105', 'medical reimbursement plan'],
    plainEnglish:
      'Reimburses employees for out-of-pocket medical costs tax-free, under a written plan. But if you own >2% of an S-corp, your OWN reimbursements are NOT tax-free — they’re W-2 wages you deduct above-the-line (§162(l)). The tax-free benefit is for non-family W-2 employees.',
    technical:
      'IRC §105(b) · requires a written plan · a >2% S-corp shareholder is treated as a partner under IRC §1372 (and a spouse is attributed that ownership under §318), so their reimbursements are taxable W-2 wages + a §162(l) deduction, not tax-free §105.',
    ircRef: 'IRC §105(b)',
    url: 'https://www.irs.gov/publications/p15b',
  },
  {
    key: 'section-179',
    term: '§179',
    aliases: ['Section 179', '179 expensing'],
    expansion: 'Section 179 expensing',
    plainEnglish:
      'Lets you deduct the whole cost of new equipment this year instead of spreading it over many years. Catch: you need enough business profit to use it — in a loss year it waits (“carries forward”) until a profitable year.',
    technical:
      'IRC §179 election · 2026 federal cap $2.56M · some states cap lower (CA/NJ $25K).',
    ircRef: 'IRC §179',
    url: 'https://www.irs.gov/publications/p946',
  },
  {
    key: 'solo-401k',
    term: 'Solo 401(k)',
    aliases: ['Individual 401(k)', 'Solo 401k', 'one-participant 401(k)'],
    plainEnglish:
      'A 401(k) for a business with no employees besides the owner (and spouse). You contribute twice — as employee AND as employer — so the ceilings are much higher than an IRA’s.',
    technical:
      '2026: $24,500 employee deferral + $8,000 catch-up (50+) or $11,250 (ages 60–63), plus employer contributions up to 20% of net self-employment income · IRC §401(k); IRS Pub 560.',
    ircRef: 'IRC §401(k)',
    url: 'https://www.irs.gov/retirement-plans/one-participant-401k-plans',
  },
  {
    key: 'standard-deduction',
    term: 'standard deduction',
    plainEnglish:
      'A flat amount everyone may subtract from income before tax — no receipts needed. You take this OR itemize your actual deductions, whichever is bigger.',
    technical:
      'IRC §63(c) · 2026: $16,100 single / $32,200 married filing jointly / $24,150 head of household (Rev. Proc. 2025-32).',
    ircRef: 'IRC §63(c)',
    url: 'https://www.irs.gov/taxtopics/tc551',
  },
  {
    key: 'straight-line',
    term: 'straight-line',
    aliases: ['straight line'],
    plainEnglish:
      'The simplest depreciation: the same deduction every year across the asset’s life. Bookkeeping usually uses it because it shows steady, realistic wear — while the tax return uses faster methods up front.',
    technical:
      'The book-side default · also an available tax election under IRC §168(b)(3).',
    ircRef: 'IRC §168(b)(3)',
    url: 'https://www.irs.gov/publications/p946',
  },

  // --- Payroll & forms ---
  {
    key: '1099-div',
    term: '1099-DIV',
    expansion: 'Dividends and Distributions',
    plainEnglish:
      'The slip a C-corporation files when it pays dividends to shareholders. It’s how the IRS knows about the second layer of the “double tax” — the recipient reports the dividends on their personal return.',
    technical:
      'IRC §6042 · qualified dividends are taxed at capital-gains rates on the recipient’s return · recipient copy due January 31.',
    ircRef: 'IRC §6042',
    url: 'https://www.irs.gov/forms-pubs/about-form-1099-div',
  },
  {
    key: '1099-int',
    term: '1099-INT',
    expansion: 'Interest Income',
    plainEnglish:
      'The slip reporting interest your business paid someone during the year — for example, interest on a shareholder or family loan. The recipient owes income tax on it; the form keeps both sides’ returns matching.',
    technical:
      'IRC §6049 · recipient copy due January 31.',
    ircRef: 'IRC §6049',
    url: 'https://www.irs.gov/forms-pubs/about-form-1099-int',
  },
  {
    key: '1099-misc',
    term: '1099-MISC',
    aliases: ['§6041'],
    expansion: 'Miscellaneous Information',
    plainEnglish:
      'The year-end slip for non-contractor business payments — rent to a landlord, prizes, legal settlements. Contractor pay moved off this form to the 1099-NEC in 2020; the same $2,000 threshold applies here for 2026.',
    technical:
      'IRC §6041 · OBBBA §70433 raised the reporting threshold to $2,000 for TY 2026 · recipient copy due January 31.',
    ircRef: 'IRC §6041',
    url: 'https://www.irs.gov/forms-pubs/about-form-1099-misc',
  },
  {
    key: '1099-nec',
    term: '1099-NEC',
    aliases: ['§6041A'],
    expansion: 'Nonemployee Compensation',
    plainEnglish:
      'The year-end slip you file for each contractor you paid $2,000 or more during 2026 (it was $600 before). One copy to the contractor, one to the IRS, by January 31.',
    technical:
      'IRC §6041A · threshold raised to $2,000 for TY 2026 by OBBBA §70433, inflation-indexed from 2027 · no automatic extension on the 1/31 deadline.',
    ircRef: 'IRC §6041A',
    url: 'https://www.irs.gov/forms-pubs/about-form-1099-nec',
  },
  {
    key: '1099-r',
    term: '1099-R',
    expansion: 'Distributions From Pensions, Annuities & Retirement Plans',
    plainEnglish:
      'The slip reporting money taken out of a retirement plan — including your Solo 401(k). Even a rollover that owes no tax gets reported on one; a code on the form tells the IRS which kind of withdrawal it was.',
    technical:
      'IRC §6047 · Box 7 distribution codes distinguish rollovers, early withdrawals, and normal distributions.',
    ircRef: 'IRC §6047',
    url: 'https://www.irs.gov/forms-pubs/about-form-1099-r',
  },
  {
    key: 'backup-withholding',
    term: 'backup withholding',
    aliases: ['§3406'],
    plainEnglish:
      'If a contractor won’t give you a valid tax ID (W-9), you’re required to hold back 24% of their pay and send it to the IRS. Collecting the W-9 up front avoids the whole problem.',
    technical:
      'IRC §3406 · flat 24% · withheld amounts are deposited, reported annually on Form 945, and shown in Box 4 of the contractor’s 1099.',
    ircRef: 'IRC §3406',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/backup-withholding',
  },
  {
    key: 'fica',
    term: 'FICA',
    expansion: 'Federal Insurance Contributions Act',
    plainEnglish:
      'The Social Security and Medicare taxes taken from every paycheck. You and your company each pay half — 7.65% apiece. As an owner-employee, your business pays the employer half on your own salary.',
    technical:
      'IRC §3101 (employee) / §3111 (employer) · 6.2% Social Security each up to the $184,500 wage base (2026) + 1.45% Medicare each with no cap.',
    ircRef: 'IRC §§3101, 3111',
    url: 'https://www.irs.gov/taxtopics/tc751',
    caseSensitive: true,
  },
  {
    key: 'fire',
    term: 'FIRE',
    expansion: 'Filing Information Returns Electronically',
    plainEnglish:
      'The IRS’s online system for filing 1099s electronically. Nexus produces files in its exact format; e-filing is required once you cross the form-count threshold, and faster than mailing paper either way.',
    technical:
      'File format defined by IRS Pub 1220 · submitting requires a Transmitter Control Code (TCC).',
    ircRef: 'IRS Pub 1220',
    url: 'https://www.irs.gov/e-file-providers/filing-information-returns-electronically-fire',
    caseSensitive: true,
  },
  {
    key: 'form-1065',
    term: 'Form 1065',
    expansion: 'U.S. Return of Partnership Income',
    plainEnglish:
      'The annual return a partnership (including multi-member LLCs) files. Like the S-corp return, it reports profit and hands each partner a K-1 — the partnership itself pays no federal income tax.',
    technical:
      'Due March 15 for calendar-year filers (six-month extension available via Form 7004) · informational return; tax is paid on the partners’ personal returns.',
    url: 'https://www.irs.gov/forms-pubs/about-form-1065',
  },
  {
    key: 'form-1120',
    term: 'Form 1120',
    expansion: 'U.S. Corporation Income Tax Return',
    plainEnglish:
      'The annual income tax return a C-corporation files for itself. The company pays its own tax with this form — owners’ personal returns only see what was paid out to them.',
    technical:
      'Due April 15 for calendar-year corporations · tax computed at the flat 21% rate (IRC §11).',
    url: 'https://www.irs.gov/forms-pubs/about-form-1120',
  },
  {
    key: 'form-1120s',
    term: 'Form 1120-S',
    aliases: ['1120-S', '1120S'],
    expansion: 'U.S. Income Tax Return for an S Corporation',
    plainEnglish:
      'The annual return an S-corp files. It usually shows no tax due — its job is reporting the profit and splitting it among owners via K-1s, which they file with their personal returns.',
    technical:
      'Due March 15 for calendar-year filers, a month before personal returns so K-1s arrive in time (six-month extension via Form 7004) · late filing penalties accrue per owner, per month.',
    url: 'https://www.irs.gov/forms-pubs/about-form-1120-s',
  },
  {
    key: 'form-2210',
    term: 'Form 2210',
    expansion: 'Underpayment of Estimated Tax by Individuals',
    plainEnglish:
      'The worksheet that calculates the penalty when your withholding + quarterly payments came in too low. If you met a safe harbor, this form is exactly how you show the IRS that no penalty applies.',
    technical:
      'Attaches to Form 1040 · penalty accrues per quarter at the federal interest rate (IRC §6654).',
    ircRef: 'IRC §6654',
    url: 'https://www.irs.gov/forms-pubs/about-form-2210',
  },
  {
    key: 'form-2553',
    term: 'Form 2553',
    expansion: 'Election by a Small Business Corporation',
    plainEnglish:
      'The form that turns your LLC or corporation into an S-corp for tax purposes. Timing matters: generally file within 2 months and 15 days of the start of the tax year you want it to apply to.',
    technical:
      'IRC §1362 election · late-election relief available under Rev. Proc. 2013-30.',
    ircRef: 'IRC §1362',
    url: 'https://www.irs.gov/forms-pubs/about-form-2553',
  },
  {
    key: 'form-5500-ez',
    term: 'Form 5500-EZ',
    aliases: ['5500-EZ'],
    expansion: 'Annual Return of a One-Participant Retirement Plan',
    plainEnglish:
      'The annual report for a one-person retirement plan like a Solo 401(k) — but only once the plan holds more than $250,000, or in its final year. Below that, nothing to file.',
    technical:
      'Due July 31 for calendar-year plans · late filings face per-day penalties, but an IRS penalty-relief program exists.',
    url: 'https://www.irs.gov/forms-pubs/about-form-5500-ez',
  },
  {
    key: 'form-940',
    term: 'Form 940',
    expansion: 'Employer’s Annual Federal Unemployment (FUTA) Tax Return',
    plainEnglish:
      'The once-a-year form reporting your federal unemployment tax. Small dollars (usually about $42 per employee) but the filing itself is mandatory for any business with employees.',
    technical:
      'Due January 31 for the prior year · the tax is employer-paid only (IRC §3301).',
    url: 'https://www.irs.gov/forms-pubs/about-form-940',
  },
  {
    key: 'form-941',
    term: 'Form 941',
    expansion: 'Employer’s Quarterly Federal Tax Return',
    plainEnglish:
      'The quarterly form telling the IRS about your payroll: what you paid employees and the taxes withheld. If a payroll company runs your payroll, they usually file it — this is your check that it happened.',
    technical:
      'Reports wages + withheld income tax + both halves of Social Security/Medicare (FICA).',
    url: 'https://www.irs.gov/forms-pubs/about-form-941',
  },
  {
    key: 'form-945',
    term: 'Form 945',
    expansion: 'Annual Return of Withheld Federal Income Tax',
    plainEnglish:
      'The annual form for tax you withheld OUTSIDE of payroll — mostly the 24% backup withholding held from contractors who never gave you a valid tax ID.',
    technical:
      'Due January 31 · only required for years you actually withheld nonpayroll tax.',
    url: 'https://www.irs.gov/forms-pubs/about-form-945',
  },
  {
    key: 'futa',
    term: 'FUTA',
    expansion: 'Federal Unemployment Tax Act',
    plainEnglish:
      'A small federal payroll tax your business pays (employees pay nothing) that funds unemployment benefits. Usually about $42 per employee per year once the state credit applies.',
    technical:
      'IRC §3301 · 6.0% on the first $7,000 of each employee’s wages, reduced to 0.6% with the full state credit · reported annually on Form 940.',
    ircRef: 'IRC §3301',
    url: 'https://www.irs.gov/taxtopics/tc759',
    caseSensitive: true,
  },
  {
    key: 'i-9',
    term: 'I-9',
    expansion: 'Employment Eligibility Verification',
    plainEnglish:
      'The form verifying a new hire is legally allowed to work in the U.S. Both of you complete it within the first 3 days of work, and you keep it on file — it’s never sent anywhere unless an inspector asks.',
    technical:
      'U.S. Citizenship and Immigration Services form · retain 3 years after hire or 1 year after separation, whichever is later.',
    url: 'https://www.uscis.gov/i-9',
  },
  {
    key: 'irc',
    term: 'IRC',
    expansion: 'Internal Revenue Code',
    plainEnglish:
      'THE federal tax law — one giant statute organized by section numbers. When you see “§179” or “§199A” anywhere in Nexus, that’s an address inside this code, so you can check the actual law behind a number.',
    technical: 'Title 26 of the U.S. Code · sections cited as IRC §<number>.',
    url: 'https://www.law.cornell.edu/uscode/text/26',
    caseSensitive: true,
  },
  {
    key: 'orphan',
    term: 'orphan',
    plainEnglish:
      'A credit-card charge Nexus could not tie to any expense you already logged — nothing close in amount or date. It usually means the expense was never recorded, so you likely need to add it (Nexus checks for a possible duplicate first).',
    technical:
      'Statement-reconciliation match tier: a line with zero scoring candidates in the ±window. Distinct from “No-match” (a candidate existed but scored too low).',
  },
  {
    key: 'release-for-export',
    term: 'release for export',
    plainEnglish:
      'A final “yes, include this on the CPA export” confirmation for a card charge that’s already matched. Matching is automatic; releasing is your deliberate sign-off, so nothing lands in the accountant bundle until you say so.',
    technical:
      'cc_statement_line_items.released_for_export — an explicit per-line flag, separate from match_status, that gates Phase-10 CPA export inclusion.',
  },
  {
    key: 'rev-proc',
    term: 'Rev. Proc.',
    aliases: ['Revenue Procedure'],
    expansion: 'Revenue Procedure',
    plainEnglish:
      'An official IRS rulebook entry describing how to do something — including the annual one that sets each year’s inflation-adjusted numbers (brackets, deductions, caps). When Nexus cites one, that’s the primary source for a dollar figure.',
    technical:
      'E.g. Rev. Proc. 2025-32 sets the 2026 inflation adjustments · published in the Internal Revenue Bulletin.',
    url: 'https://www.irs.gov/internal-revenue-bulletins',
  },
  {
    key: 'schedule-se',
    term: 'Schedule SE',
    plainEnglish:
      'The worksheet attached to your personal return that computes self-employment tax — the Social Security and Medicare you owe on business profit that didn’t come through a paycheck.',
    technical:
      'IRC §1401 · 15.3% on 92.35% of net self-employment earnings · attaches to Form 1040.',
    ircRef: 'IRC §1401',
    url: 'https://www.irs.gov/forms-pubs/about-schedule-se-form-1040',
  },
  {
    key: 'w-2',
    term: 'W-2',
    expansion: 'Wage and Tax Statement',
    plainEnglish:
      'The annual statement of what an employee was paid and what taxes were withheld. As an S-corp owner you get one too — your own salary is W-2 wages, which is exactly what proves your reasonable compensation.',
    technical:
      'Issued by January 31 · filed with the Social Security Administration · typically generated by your payroll provider.',
    url: 'https://www.irs.gov/forms-pubs/about-form-w-2',
  },
  {
    key: 'w-4',
    term: 'W-4',
    expansion: 'Employee’s Withholding Certificate',
    plainEnglish:
      'The form an employee fills out at hire that sets how much federal income tax is withheld from each paycheck. Update it after life changes (marriage, a second job) so April holds no surprises.',
    technical:
      'Dollar-based adjustments since the 2020 redesign (no more “allowances”) · can be updated any time.',
    url: 'https://www.irs.gov/forms-pubs/about-form-w-4',
  },
  {
    key: 'w-9',
    term: 'W-9',
    expansion: 'Request for Taxpayer Identification Number and Certification',
    plainEnglish:
      'The form you collect FROM a contractor before paying them — it gives you their legal name and tax ID so you can file their 1099 at year-end. No W-9 on file means you must withhold 24% of their pay.',
    technical:
      'Collect before first payment · feeds 1099-NEC/MISC filing · a missing or invalid TIN triggers IRC §3406 backup withholding.',
    url: 'https://www.irs.gov/forms-pubs/about-form-w-9',
  },
  {
    key: 'withholding',
    term: 'withholding',
    aliases: ['tax withholding'],
    plainEnglish:
      'Tax taken out of a paycheck before you see it and sent to the IRS on your behalf. It’s a prepayment, not an extra tax — your return settles up the difference in April.',
    technical:
      'IRC §3402 · set by your W-4 · for owners, raising W-2 withholding late in the year can cover an estimated-tax shortfall.',
    ircRef: 'IRC §3402',
    url: 'https://www.irs.gov/payments/tax-withholding',
  },

  // --- Accounting ---
  {
    key: 'accounts-payable',
    term: 'A/P',
    aliases: ['accounts payable'],
    expansion: 'Accounts Payable',
    plainEnglish:
      'Money you owe suppliers — bills received but not yet paid. Managing the timing is free financing: pay on the due date, not early, without ever paying late.',
    technical:
      'A liability on the balance sheet · the mirror image of the vendor’s accounts receivable.',
    caseSensitive: true,
  },
  {
    key: 'accounts-receivable',
    term: 'A/R',
    aliases: ['accounts receivable'],
    expansion: 'Accounts Receivable',
    plainEnglish:
      'Money customers owe you — invoices sent but not yet paid. It’s yours on paper, not in the bank; watching it age tells you who to chase before cash flow hurts.',
    technical:
      'An asset on the balance sheet · “aging” buckets (30/60/90 days) flag collection risk.',
    caseSensitive: true,
  },
  {
    key: 'accrual-accounting',
    term: 'accrual accounting',
    aliases: ['accrual basis'],
    plainEnglish:
      'Counting income when it’s EARNED and expenses when they’re INCURRED — not when cash moves. An invoice you sent counts as revenue today even if the client pays next month. The alternative (cash basis) counts only actual money movement.',
    technical:
      'IRC §446 accounting methods · most small businesses may use cash basis for tax; accrual gives truer monthly statements.',
    ircRef: 'IRC §446',
    url: 'https://www.irs.gov/publications/p538',
  },
  {
    key: 'balance-sheet',
    term: 'balance sheet',
    plainEnglish:
      'A snapshot of what the business OWNS (cash, equipment, money owed to you) versus what it OWES, at one moment in time. The difference is your equity — and the two sides must always balance, hence the name.',
    technical:
      'Assets = liabilities + equity · the P&L covers a period; the balance sheet is a point-in-time photo.',
  },
  {
    key: 'book-vs-tax',
    term: 'book vs tax',
    aliases: ['book income', 'book-vs-tax'],
    plainEnglish:
      'Your books and your tax return measure profit under different rules, on purpose. The books aim at economic reality; the return follows tax law (faster equipment write-offs, 50% meal deductions). Neither number is “wrong” — they answer different questions.',
    technical:
      'Differences are reconciled on Schedule M-1 · depreciation method drift is the most common gap.',
  },
  {
    key: 'ebitda',
    term: 'EBITDA',
    expansion: 'Earnings Before Interest, Taxes, Depreciation & Amortization',
    plainEnglish:
      'A profit measure that strips out financing and paper costs to show how the core business performs. Buyers and lenders love it — a business’s sale price is often quoted as a multiple of it.',
    technical:
      'A management metric, not a tax or GAAP figure · revenue − operating expenses, before interest, taxes, depreciation, and amortization.',
    caseSensitive: true,
  },
  {
    key: 'fmv',
    term: 'FMV',
    aliases: ['fair market value'],
    expansion: 'Fair Market Value',
    plainEnglish:
      'What something would sell for between a willing buyer and a willing seller — nobody forced, both informed. It’s the IRS’s yardstick everywhere: your salary benchmark, asset values, and related-party deals all get tested against it.',
    technical:
      'Drives reasonable-compensation benchmarks (MGMA/BLS data), asset valuations, and related-party transaction pricing.',
    caseSensitive: true,
  },
  {
    key: 'p-and-l',
    term: 'P&L',
    aliases: ['profit and loss', 'income statement', 'profit & loss'],
    expansion: 'Profit & Loss statement',
    plainEnglish:
      'The report showing what you earned, what you spent, and what’s left, over a period. The single most useful statement for running the business — and the starting point for your tax return.',
    technical:
      'Also called the income statement · revenue − expenses = net income · pairs with the balance sheet.',
  },
  {
    key: 'schedule-m1',
    term: 'Schedule M-1',
    aliases: ['M-1'],
    plainEnglish:
      'The section of a business tax return that explains why your bookkeeping profit and your taxable profit differ. Differences are normal — meals are only half deductible, depreciation runs at different speeds — and this schedule shows the bridge.',
    technical:
      'Appears on Forms 1120, 1120-S, and 1065 · reconciles book income to tax income line by line.',
    url: 'https://www.irs.gov/forms-pubs/about-form-1120-s',
  },

  // --- State ---
  {
    key: 'apportionment',
    term: 'apportionment',
    aliases: ['multi-state apportionment', 'state apportionment'],
    plainEnglish:
      'When you earn income in more than one state, each state taxes its fair slice — apportionment is the math that splits your income among them (often by days worked or sales in each state).',
    technical:
      'Method varies by state — days-worked is common for personal-service income · drives multi-state filing obligations.',
  },
  {
    key: 'franchise-tax',
    term: 'franchise tax',
    plainEnglish:
      'A fee some states charge for the privilege of existing or doing business there — owed even in a no-profit year. Despite the name it has nothing to do with franchising; Delaware and Texas are famous for it.',
    technical:
      'Base varies by state — flat fee, net worth, or gross receipts · separate from (and on top of) any state income tax.',
  },
  {
    key: 'nj-bait',
    term: 'NJ BAIT',
    aliases: ['BAIT', 'Business Alternative Income Tax'],
    expansion: 'Business Alternative Income Tax',
    plainEnglish:
      'New Jersey’s version of the pass-through entity tax workaround: your S-corp or partnership elects to pay NJ tax at the company level, restoring the full federal deduction that the personal-return cap would deny.',
    technical:
      'Annual election + quarterly estimated payments at the entity level · owners claim a matching credit on their NJ returns.',
    url: 'https://www.nj.gov/treasury/taxation/baitpte/index.shtml',
    caseSensitive: true,
  },
  {
    key: 'ptet',
    term: 'PTET',
    aliases: ['pass-through entity tax'],
    expansion: 'Pass-Through Entity Tax',
    plainEnglish:
      'A state-level workaround for the federal cap on deducting state taxes: the BUSINESS elects to pay the state income tax itself and deducts it federally without any cap — most owners come out ahead.',
    technical:
      'IRS-blessed in Notice 2020-75 · most states with an income tax now offer an election · usually requires opting in by a deadline and making entity-level estimated payments.',
    ircRef: 'IRS Notice 2020-75',
    url: 'https://www.irs.gov/pub/irs-drop/n-20-75.pdf',
    caseSensitive: true,
  },
  {
    key: 'state-nexus',
    term: 'state nexus',
    aliases: ['multi-state nexus', 'tax nexus'],
    plainEnglish:
      'Enough connection to a state that it can tax you or make you file there. Working days, an office, an employee, or enough sales in a state can each create it — often without you noticing.',
    technical:
      'Triggered by physical presence or economic thresholds · creates registration, filing, and withholding obligations in that state.',
  },

  // --- Credentials & continuing education (2026-06-15 app-wide coverage) ---
  {
    key: 'continuing-education',
    term: 'CE',
    aliases: ['continuing education', 'CE hours', 'CE credits'],
    expansion: 'Continuing Education',
    plainEnglish:
      'The credit hours a licensed professional must earn each renewal cycle to keep a license active — called CME for doctors, CLE for lawyers, CPE for CPAs, PDH for engineers. Nexus tracks the hours, the deadline, and the certificates.',
    technical:
      'Requirement + hours + reporting period set by each state board / licensing body · logged against a credential on the Credentials page.',
    caseSensitive: true,
  },
  {
    key: 'cme',
    term: 'CME',
    expansion: 'Continuing Medical Education',
    plainEnglish:
      'The continuing-education credit hours a physician must earn each renewal cycle to keep a medical license active. The required number and cycle length are set by your state medical board.',
    technical:
      'A form of continuing education (CE) · hours + cycle set per state medical board · logged against a medical-license credential.',
    caseSensitive: true,
  },
  {
    key: 'cle',
    term: 'CLE',
    expansion: 'Continuing Legal Education',
    plainEnglish:
      'The continuing-education credit hours an attorney must earn each cycle to keep a bar license active — often including an ethics component. The number and cycle are set by each state bar.',
    technical:
      'A form of continuing education (CE) · hours + cycle (often with an ethics/professionalism minimum) set per state bar · logged against a bar-admission credential.',
    caseSensitive: true,
  },
  {
    key: 'cpe',
    term: 'CPE',
    expansion: 'Continuing Professional Education',
    plainEnglish:
      'The continuing-education credit hours a CPA must earn each cycle to keep an accounting license active. The required number and cycle are set by each state board of accountancy.',
    technical:
      'A form of continuing education (CE) · hours + cycle set per state board of accountancy · logged against a CPA-license credential.',
    caseSensitive: true,
  },
  {
    key: 'pdh',
    term: 'PDH',
    aliases: ['professional development hours'],
    expansion: 'Professional Development Hours',
    plainEnglish:
      'The continuing-education credit hours a licensed professional engineer (and some other licensed trades) must earn each renewal cycle to keep the license active.',
    technical:
      'A form of continuing education (CE) · hours + cycle set per state engineering/licensing board · 1 PDH generally equals 1 contact hour.',
    caseSensitive: true,
  },
  {
    key: 'dea-registration',
    term: 'DEA',
    aliases: ['DEA registration', 'DEA number'],
    expansion: 'Drug Enforcement Administration registration',
    plainEnglish:
      'The federal registration a prescriber must hold to prescribe or dispense controlled substances. It renews on its own cycle, separate from your state license — letting it lapse stops you prescribing.',
    technical:
      'Issued by the U.S. DEA under the Controlled Substances Act · typically a 3-year renewal · tracked as a credential with its own expiration.',
    url: 'https://www.deadiversion.usdoj.gov/',
    caseSensitive: true,
  },
  {
    key: 'imlc',
    term: 'IMLC',
    expansion: 'Interstate Medical Licensure Compact',
    plainEnglish:
      'A faster path for a physician to get licensed in several states. You qualify through one home “state of principal licensure,” then obtain full licenses in other member states without starting over each time.',
    technical:
      'Agreement among participating state medical boards · expedites (does not replace) full licensure in each member state · tracked as a compact on a medical credential.',
    caseSensitive: true,
  },
  {
    key: 'nlc',
    term: 'NLC',
    expansion: 'Nurse Licensure Compact',
    plainEnglish:
      'An agreement that lets a nurse hold one multi-state license and practice in any other member state without a separate license for each.',
    technical:
      'Multi-state license issued by the nurse’s home (primary-residence) state · the privilege to practice extends to all NLC member states · tracked as a compact on a nursing credential.',
    caseSensitive: true,
  },
  {
    key: 'ube',
    term: 'UBE',
    expansion: 'Uniform Bar Examination',
    plainEnglish:
      'A standardized bar exam whose score is portable to other participating states, so an attorney can be admitted in multiple jurisdictions without re-taking the test.',
    technical:
      'Developed by the National Conference of Bar Examiners · produces a portable score (not a license) · each jurisdiction sets its own passing score and admission rules.',
    caseSensitive: true,
  },
  {
    key: 'ncees',
    term: 'NCEES',
    expansion: 'National Council of Examiners for Engineering and Surveying',
    plainEnglish:
      'The body behind the engineering licensure exams. Its “Record” service stores your credentials so a licensed engineer can get licensed in additional states more easily.',
    technical:
      'Administers the FE and PE exams · the NCEES Record streamlines comity/reciprocity licensure across state engineering boards.',
    caseSensitive: true,
  },
  {
    key: 'ncarb',
    term: 'NCARB',
    expansion: 'National Council of Architectural Registration Boards',
    plainEnglish:
      'The body behind architect licensure. Its “Certificate” makes it easier for a licensed architect to obtain reciprocal licenses in other states.',
    technical:
      'Administers the Architect Registration Examination (ARE) · the NCARB Certificate supports reciprocity across state architecture boards.',
    caseSensitive: true,
  },
  {
    key: 'psypact',
    term: 'PSYPACT',
    expansion: 'Psychology Interjurisdictional Compact',
    plainEnglish:
      'An agreement that lets a licensed psychologist practice across member states — especially telehealth — without a separate license in each one.',
    technical:
      'Authorizes telepsychology and temporary in-person practice across PSYPACT member states · tracked as a compact on a psychology credential.',
  },
  {
    key: 'pto',
    term: 'PTO',
    aliases: ['paid time off'],
    expansion: 'Paid Time Off',
    plainEnglish:
      'Leave an employee earns and can take while still being paid — vacation and sick time, often pooled into one balance. Nexus tracks how much each employee has accrued and used.',
    technical:
      'Accrual rate, carryover, and payout-on-termination are governed by company policy and, for sick time, a growing set of state mandates.',
    caseSensitive: true,
  },
  {
    key: 'flsa',
    term: 'FLSA',
    expansion: 'Fair Labor Standards Act',
    plainEnglish:
      'The federal wage-and-hour law: it sets minimum wage, overtime, and who is “exempt” (salaried, no overtime) versus “non-exempt” (hourly, overtime-eligible). Misclassifying someone is a common, costly mistake.',
    technical:
      'Enforced by the U.S. DOL Wage and Hour Division · the salary threshold for most exempt employees is $35,568/yr ($684/week) after the 2024 increase was vacated in court.',
    url: 'https://www.dol.gov/agencies/whd/flsa',
    caseSensitive: true,
  },
  {
    key: 'coi',
    term: 'COI',
    aliases: ['certificate of insurance'],
    expansion: 'Certificate of Insurance',
    plainEnglish:
      'A one-page proof that a contractor or vendor actually carries the insurance they claim. You collect it before work starts so their accident doesn’t become your liability.',
    technical:
      'Issued by the vendor’s insurer · lists coverage types, limits, and policy dates · often required to name you as an additional insured.',
    caseSensitive: true,
  },
  {
    key: 'roth',
    term: 'Roth',
    aliases: ['Roth account', 'Roth IRA', 'Roth 401(k)'],
    plainEnglish:
      'A retirement account you fund with already-taxed money, so qualified withdrawals in retirement — including all the growth — come out completely tax-free. The opposite of a traditional (pre-tax) account.',
    technical:
      'IRC §408A (Roth IRA) / §402A (designated Roth) · no deduction now; qualified distributions are tax-free after age 59½ and a 5-year holding period.',
    ircRef: 'IRC §408A',
    url: 'https://www.irs.gov/retirement-plans/roth-iras',
  },

  // --- Statutes, forms & business abbreviations (Batch 2, 2026-06-15 app-wide coverage) ---
  {
    key: 'section-1031',
    term: '§1031',
    aliases: ['Section 1031', 'like-kind exchange'],
    expansion: 'Section 1031 like-kind exchange',
    plainEnglish:
      'Lets you swap one piece of business or investment real estate for another and defer the tax on the gain — you roll it into the new property instead of paying now. Since 2018 it only works for real estate, not equipment.',
    technical:
      'IRC §1031 · real property only after the 2017 tax law · strict 45-day identification and 180-day closing deadlines.',
    ircRef: 'IRC §1031',
    url: 'https://www.law.cornell.edu/uscode/text/26/1031',
  },
  {
    key: 'irc-264',
    term: '§264(a)(1)',
    aliases: ['§264', 'Section 264'],
    plainEnglish:
      'Blocks a business from deducting life-insurance premiums when the business itself is the beneficiary — for example, key-person or officer life insurance. Nexus flags these so they’re added back on your tax return.',
    technical:
      'IRC §264(a)(1) · premiums non-deductible where the taxpayer is directly or indirectly a beneficiary · reconciled as a book-vs-tax add-back on Schedule M-1.',
    ircRef: 'IRC §264(a)(1)',
    url: 'https://www.law.cornell.edu/uscode/text/26/264',
  },
  {
    key: 'irc-6001',
    term: '§6001',
    aliases: ['IRC §6001', 'Section 6001'],
    plainEnglish:
      'The law that simply requires you to keep records backing up everything on your tax return. It’s why Nexus stores your receipts and documents — and why the retention reminder exists: hold them generally 7 years.',
    technical:
      'IRC §6001 · recordkeeping requirement · retain supporting records while the return stays open to audit (commonly 7 years).',
    ircRef: 'IRC §6001',
    url: 'https://www.law.cornell.edu/uscode/text/26/6001',
  },
  {
    key: 'irc-6041h',
    term: '§6041(h)',
    aliases: ['IRC §6041(h)'],
    plainEnglish:
      'Starting in 2027, this automatically raises the 1099 reporting threshold for inflation each year — so the $2,000 trigger for filing a 1099 won’t stay frozen the way the old $600 one did for decades.',
    technical:
      'IRC §6041(h), added by OBBBA P.L. 119-21 §70433(b) · indexes the §6041 / §6041A information-return thresholds for years after 2026.',
    ircRef: 'IRC §6041(h)',
    url: 'https://www.law.cornell.edu/uscode/text/26/6041',
  },
  {
    key: 'obbba',
    term: 'OBBBA',
    expansion: 'One Big Beautiful Bill Act',
    plainEnglish:
      'The 2025 federal tax law that reset many 2026 numbers — tax brackets, the SALT cap, the standard deduction, equipment write-offs, and the 1099 filing thresholds. When Nexus cites it, it’s explaining why a figure changed this year.',
    technical:
      'P.L. 119-21, enacted 2025 · source of the OBBBA-adjusted 2026 constants used throughout Nexus.',
    caseSensitive: true,
  },
  {
    key: 'eftps',
    term: 'EFTPS',
    expansion: 'Electronic Federal Tax Payment System',
    plainEnglish:
      'The free government website for paying federal taxes electronically — payroll deposits, backup withholding, estimated taxes. There’s no automatic feed, so you pay on EFTPS and record the confirmation number in Nexus.',
    technical:
      'Run by the U.S. Treasury · enroll at eftps.gov · each payment returns a 15-digit confirmation number.',
    url: 'https://www.eftps.gov/',
    caseSensitive: true,
  },
  {
    key: 'form-945-a',
    term: 'Form 945-A',
    aliases: ['945-A'],
    expansion: 'Annual Record of Federal Tax Liability',
    plainEnglish:
      'A schedule attached to Form 945 that breaks your federal tax liability down day-by-day. You only need it if you were a semiweekly depositor or had a large single deposit; most small filers skip it.',
    technical:
      'Daily liability detail for semiweekly-schedule depositors · attaches to Form 945.',
  },
  {
    key: 'form-945-v',
    term: 'Form 945-V',
    aliases: ['945-V'],
    expansion: 'Form 945 Payment Voucher',
    plainEnglish:
      'The payment slip you mail with a check when a Form 945 has a small balance due. Most filers pay electronically through EFTPS and never use it.',
    technical:
      'Detached and mailed with a check for a Form 945 balance under the electronic-payment threshold.',
  },
  {
    key: 'pub-1220',
    term: 'Pub 1220',
    aliases: ['Publication 1220'],
    plainEnglish:
      'The IRS rulebook defining the exact file format for e-filing 1099s. Nexus generates a file in this format so you can upload it to the IRS FIRE system instead of mailing paper.',
    technical:
      'Fixed-width record specification for electronic information-return filing · the format the FIRE system accepts.',
  },
  {
    key: 'cusip',
    term: 'CUSIP',
    plainEnglish:
      'The 9-character ID that uniquely identifies a specific stock or bond. On a 1099 it pins down exactly which tax-exempt bond paid the interest you’re reporting.',
    technical:
      'Standard securities identifier · printed on 1099-INT / 1099-DIV for tax-exempt bond interest.',
    caseSensitive: true,
  },
  {
    key: 'fatca',
    term: 'FATCA',
    expansion: 'Foreign Account Tax Compliance Act',
    plainEnglish:
      'A U.S. law making foreign banks report Americans’ overseas accounts. On a 1099, the “FATCA filing requirement” box just flags that the form satisfies that reporting — most domestic businesses leave it unchecked.',
    technical:
      'Reported via the 1099 FATCA-filing-requirement checkbox · large foreign-account holders may also owe Form 8938.',
    url: 'https://www.irs.gov/businesses/corporations/foreign-account-tax-compliance-act-fatca',
    caseSensitive: true,
  },
  {
    key: 'pab',
    term: 'PAB',
    expansion: 'Private Activity Bond',
    plainEnglish:
      'A municipal bond that funds a private project (a stadium, a hospital). Interest on certain ones is tax-free for regular tax but counts toward the alternative minimum tax — which is why 1099s report it separately.',
    technical:
      'Interest on specified private activity bonds is an AMT preference item (IRC §57(a)(5)) · reported in its own 1099-DIV / 1099-INT box.',
    caseSensitive: true,
  },
  {
    key: 'cp-575',
    term: 'CP 575',
    plainEnglish:
      'The confirmation letter the IRS mails when it assigns your business its EIN. It’s the official proof of your tax ID — banks and payroll providers often ask to see it, so keep a copy.',
    technical:
      'IRS notice confirming EIN assignment · request a replacement (Letter 147C) from the IRS if lost.',
  },
  {
    key: 'boi',
    term: 'BOI',
    expansion: 'Beneficial Ownership Information',
    plainEnglish:
      'A report to FinCEN listing who really owns or controls a company. As of the March 2025 rule, companies formed in the U.S. are exempt — only foreign companies registered to do business here still have to file.',
    technical:
      'FinCEN report under the Corporate Transparency Act · the March 2025 interim final rule exempts U.S.-formed entities; foreign registrants still report.',
    url: 'https://www.fincen.gov/boi',
    caseSensitive: true,
  },
  {
    key: 'cta',
    term: 'CTA',
    expansion: 'Corporate Transparency Act',
    plainEnglish:
      'The 2021 law that created the beneficial-ownership (BOI) reporting requirement to FinCEN. After 2025 rule changes, most companies formed in the U.S. no longer have to file.',
    technical:
      '31 U.S.C. §5336 · implemented by FinCEN · the 2025 interim final rule narrowed reporting to foreign registrants.',
    url: 'https://www.fincen.gov/boi',
    caseSensitive: true,
  },
  {
    key: 'ach',
    term: 'ACH',
    expansion: 'Automated Clearing House',
    plainEnglish:
      'The bank-to-bank network that moves money electronically in the U.S. — direct deposit, online bill pay, most recurring transfers. Cheaper and slower than a wire; usually settles in 1–2 business days.',
    technical:
      'Governed by Nacha operating rules · used for payroll direct deposit, vendor payments, and tax debits.',
    caseSensitive: true,
  },
  {
    key: 'ocr',
    term: 'OCR',
    expansion: 'Optical Character Recognition',
    plainEnglish:
      'Technology that reads the text off a photo or scan, so a snapshot of a receipt becomes data Nexus can use — it’s how Smart Capture pulls the vendor, date, and amount off an uploaded receipt.',
    technical:
      'Nexus runs OCR on uploaded receipts and documents to extract structured fields automatically.',
    caseSensitive: true,
  },
  {
    key: 'api',
    term: 'API',
    expansion: 'Application Programming Interface',
    plainEnglish:
      'A way for other software to talk to Nexus directly — pull invoices, push expenses — without a person clicking around. You create an API key to let a trusted tool or your accountant’s system connect.',
    technical:
      'Authenticated with a per-entity API key · scoped to the specific read / write permissions you grant.',
    caseSensitive: true,
  },
  {
    key: 'mfa',
    term: 'MFA',
    expansion: 'Multi-Factor Authentication',
    plainEnglish:
      'A second proof of identity on top of your password — a passkey (Face ID / fingerprint) or a one-time code. Even if someone steals your password, they can’t sign in without the second factor.',
    technical:
      'Required on this account · satisfied by a passkey, an authenticator-app code (TOTP), or another enrolled factor.',
    caseSensitive: true,
  },
  {
    key: 'nda',
    term: 'NDA',
    expansion: 'Non-Disclosure Agreement',
    plainEnglish:
      'A contract where one or both sides promise to keep shared information confidential. Common before discussing a deal, hiring a contractor, or showing someone your books.',
    technical:
      'Also called a confidentiality agreement · may be one-way (one side discloses) or mutual.',
    caseSensitive: true,
  },
  {
    key: 'msa',
    term: 'MSA',
    expansion: 'Master Service Agreement',
    plainEnglish:
      'An umbrella contract setting the standard terms (payment, liability, IP) for an ongoing relationship. Each specific project then runs under it via a short statement of work, so you don’t renegotiate the basics every time.',
    technical:
      'Paired with one or more statements of work (SOWs) that define scope, deliverables, and price per engagement.',
    caseSensitive: true,
  },
  {
    key: 'sow',
    term: 'SOW',
    expansion: 'Statement of Work',
    plainEnglish:
      'The document spelling out a specific project — what’s delivered, by when, for how much. It usually rides under a master service agreement that holds the legal terms.',
    technical:
      'Defines scope, milestones, deliverables, and pricing · governed by the terms of the underlying MSA.',
    caseSensitive: true,
  },
  {
    key: 'cpa',
    term: 'CPA',
    expansion: 'Certified Public Accountant',
    plainEnglish:
      'A licensed accountant who can prepare and sign tax returns, represent you before the IRS, and issue audited financial statements. Nexus organizes your books so your CPA spends less billable time cleaning them up.',
    technical:
      'State-licensed · passed the Uniform CPA Exam · maintains continuing professional education (CPE).',
    caseSensitive: true,
  },
  {
    key: 'llc',
    term: 'LLC',
    expansion: 'Limited Liability Company',
    plainEnglish:
      'A business structure that shields your personal assets from business debts while staying simple to run. By default it’s taxed as a sole proprietorship or partnership, but it can elect S-corp or C-corp tax treatment.',
    technical:
      'Formed under state law · the liability shield depends on keeping business and personal finances separate.',
    caseSensitive: true,
  },
  {
    key: 'pllc',
    term: 'PLLC',
    expansion: 'Professional Limited Liability Company',
    plainEnglish:
      'An LLC for licensed professionals — physicians, attorneys, CPAs, architects. Same liability shield as a regular LLC, but it doesn’t cover your own malpractice; professional insurance handles that.',
    technical:
      'Required in some states for licensed practices · owners typically must hold the relevant professional license.',
    caseSensitive: true,
  },
  {
    key: 'llp',
    term: 'LLP',
    expansion: 'Limited Liability Partnership',
    plainEnglish:
      'A partnership where each partner is shielded from the other partners’ mistakes and the firm’s debts. Common for law and accounting firms with multiple owners.',
    technical:
      'Formed under state law · taxed as a partnership (files Form 1065) · partner protections vary by state.',
    caseSensitive: true,
  },

  // --- Board certification / continuing certification (P2, 2026-06-16) ---
  {
    key: 'moc',
    term: 'MOC',
    aliases: ['Maintenance of Certification', 'Continuing Certification', 'continuous certification'],
    expansion: 'Maintenance of Certification',
    plainEnglish:
      'Board certification isn’t a one-time, permanent credential anymore — you keep proving you’re current through your board’s program: continuing education, ongoing assessment, and practice improvement. Each specialty board runs its own, so there’s no single cycle.',
    technical:
      'ABMS “Standards for Continuing Certification” (effective 2024) · its 24 member boards each set their own requirements, deadlines, and fees · most replaced the 10-year recertification exam with board-specific longitudinal assessment. Confirm specifics with your issuing board.',
    caseSensitive: true,
  },
  {
    key: 'board-certification',
    term: 'board certification',
    aliases: ['board certified', 'specialty board'],
    plainEnglish:
      'A specialty credential showing a professional met their field’s national standard — passed the board’s exam and keeps up its requirements. Different from a state license: the license lets you practice; board certification signals specialty expertise.',
    technical:
      'Issued by a specialty certifying board (e.g. an ABMS member board for physicians) · maintained via the board’s continuing-certification (MOC) program · time-limited certificates require ongoing participation; older lifetime certificates may not.',
  },

  // --- Acronyms batch (2026-06-16): health, retirement, payroll, entity & regulator terms ---
  {
    key: 'fbar',
    term: 'FBAR',
    aliases: ['Report of Foreign Bank and Financial Accounts', 'FinCEN Form 114'],
    expansion: 'Foreign Bank Account Report',
    plainEnglish:
      'A yearly report you owe if your foreign bank or financial accounts together topped $10,000 at any point last year. It’s informational — no tax — but skipping it carries steep penalties. Filed online to FinCEN, separate from your tax return.',
    technical:
      '31 U.S.C. §5314 · FinCEN Form 114 · $10,000 aggregate threshold across all foreign accounts · due April 15 with an automatic extension to Oct 15.',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/report-of-foreign-bank-and-financial-accounts-fbar',
    caseSensitive: true,
  },
  {
    key: 'hsa',
    term: 'HSA',
    aliases: ['Health Savings Account'],
    expansion: 'Health Savings Account',
    plainEnglish:
      'A tax-free savings account for medical costs — but only if you’re on a qualifying high-deductible health plan. Money goes in pre-tax, grows tax-free, and comes out tax-free for medical bills. The balance rolls over every year and is yours to keep.',
    technical:
      'IRC §223 · requires HDHP coverage with no disqualifying other coverage · 2026 contribution limits set yearly by IRS Rev. Proc. · excess contributions hit a 6% excise (§4973).',
    ircRef: 'IRC §223',
    url: 'https://www.irs.gov/forms-pubs/about-publication-969',
    caseSensitive: true,
  },
  {
    key: 'hdhp',
    term: 'HDHP',
    aliases: ['High-Deductible Health Plan'],
    expansion: 'High-Deductible Health Plan',
    plainEnglish:
      'A health plan with a higher deductible and lower premiums. It’s the gateway to an HSA — you can only contribute to a Health Savings Account if your coverage qualifies as an HDHP and you have no other first-dollar coverage.',
    technical:
      'IRC §223(c)(2) · minimum-deductible + maximum-out-of-pocket limits set yearly by IRS Rev. Proc. · a general-purpose FSA/HRA or Medicare disqualifies HSA contributions.',
    ircRef: 'IRC §223(c)(2)',
    url: 'https://www.irs.gov/forms-pubs/about-publication-969',
    caseSensitive: true,
  },
  {
    key: 'hra',
    term: 'HRA',
    aliases: ['Health Reimbursement Arrangement'],
    expansion: 'Health Reimbursement Arrangement',
    plainEnglish:
      'An employer-funded plan that reimburses workers tax-free for medical costs (and sometimes premiums). The company sets the budget; you submit receipts. For a more-than-2% S-corp owner it’s generally off the table — the benefit gets taxed back to you.',
    technical:
      'IRC §105 / §106 · employer-funded, not salary-reduction · QSEHRA and ICHRA are small-employer variants · >2% S-corp shareholders are excluded from tax-free treatment (§1372).',
    ircRef: 'IRC §105',
    url: 'https://www.irs.gov/forms-pubs/about-publication-969',
    caseSensitive: true,
  },
  {
    key: 'qsehra',
    term: 'QSEHRA',
    aliases: ['Qualified Small Employer HRA'],
    expansion: 'Qualified Small Employer Health Reimbursement Arrangement',
    plainEnglish:
      'A way for a small business (under 50 employees) with no group health plan to reimburse workers tax-free for their own insurance and out-of-pocket medical costs, up to an annual cap — without buying a full group plan.',
    technical:
      'IRC §9831(d) (21st Century Cures Act) · employer-funded only · annual reimbursement caps set yearly by IRS · the employee must carry minimum essential coverage.',
    ircRef: 'IRC §9831(d)',
    caseSensitive: true,
  },
  {
    key: 'sep-ira',
    term: 'SEP-IRA',
    aliases: ['SEP', 'Simplified Employee Pension'],
    expansion: 'Simplified Employee Pension IRA',
    plainEnglish:
      'A simple retirement plan a business funds for itself (and any employees). Easy to open and high contribution room — but if you have employees, you must put in the same percentage of pay for them too, which can get expensive.',
    technical:
      'IRC §408(k) · employer contributions up to 25% of compensation (≈20% of net self-employment income), within the annual §415(c) cap · eligible employees must be covered proportionally.',
    ircRef: 'IRC §408(k)',
    url: 'https://www.irs.gov/retirement-plans/plan-sponsor/simplified-employee-pension-plan-sep',
    caseSensitive: true,
  },
  {
    key: 'simple-ira',
    term: 'SIMPLE IRA',
    aliases: ['Savings Incentive Match Plan for Employees'],
    expansion: 'Savings Incentive Match Plan for Employees IRA',
    plainEnglish:
      'A low-cost retirement plan for small businesses (100 or fewer employees): employees contribute from their pay and the employer matches. Less paperwork than a 401(k), but lower contribution limits.',
    technical:
      'IRC §408(p) · employee salary deferral + mandatory employer match (up to 3%) or 2% nonelective · 2026 deferral limits set yearly by IRS.',
    ircRef: 'IRC §408(p)',
    url: 'https://www.irs.gov/retirement-plans/plan-sponsor/simple-ira-plan',
  },
  {
    key: 'suta',
    term: 'SUTA',
    aliases: ['SUI', 'state unemployment tax', 'state unemployment insurance'],
    expansion: 'State Unemployment Tax Act',
    plainEnglish:
      'The state version of unemployment tax that employers pay on wages — it funds your state’s unemployment benefits. The rate and wage base vary by state and by your claims history. It’s separate from the federal FUTA tax.',
    technical:
      'State payroll tax (also called SUI) · employer-paid in most states · experience-rated · paying it on time earns the credit that drops federal FUTA to 0.6%.',
    caseSensitive: true,
  },
  {
    key: 'tfrp',
    term: 'TFRP',
    aliases: ['Trust Fund Recovery Penalty'],
    expansion: 'Trust Fund Recovery Penalty',
    plainEnglish:
      'A personal penalty the IRS can apply if payroll taxes withheld from employees aren’t paid to the government — even if a payroll company was supposed to do it. It pierces the company: the IRS can come after you personally.',
    technical:
      'IRC §6672 · 100% of the unpaid withheld income tax + employee FICA · applies to any “responsible person” who willfully failed to remit · survives even if the business closes.',
    ircRef: 'IRC §6672',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/trust-fund-recovery-penalty',
    caseSensitive: true,
  },
  {
    key: 'cobra',
    term: 'COBRA',
    aliases: ['Consolidated Omnibus Budget Reconciliation Act'],
    expansion: 'Consolidated Omnibus Budget Reconciliation Act',
    plainEnglish:
      'The law that lets you keep an old employer’s health plan for a while after leaving a job — you pay the full premium yourself. Common bridge coverage until a new plan or your own business insurance starts.',
    technical:
      'IRC §4980B / ERISA · typically 18 months of continuation coverage · the former employee pays up to 102% of the group premium.',
    ircRef: 'IRC §4980B',
    url: 'https://www.dol.gov/general/topic/health-plans/cobra',
    caseSensitive: true,
  },
  {
    key: 'aca',
    term: 'ACA',
    aliases: ['Affordable Care Act', 'Obamacare'],
    expansion: 'Affordable Care Act',
    plainEnglish:
      'The 2010 health-care law behind the insurance marketplaces, premium subsidies, and rules on what coverage must include. For a small business it shapes marketplace options, subsidy eligibility, and (at 50+ employees) a coverage mandate.',
    technical:
      'Patient Protection and Affordable Care Act (2010) · marketplace premium tax credit (§36B) · employer shared-responsibility applies at 50+ full-time-equivalent employees (§4980H).',
    url: 'https://www.irs.gov/affordable-care-act',
    caseSensitive: true,
  },
  {
    key: 'iolta',
    term: 'IOLTA',
    aliases: ['client trust account', 'lawyers trust account'],
    expansion: 'Interest on Lawyers’ Trust Accounts',
    plainEnglish:
      'A special bank account where attorneys hold money that belongs to clients (retainers, settlements) separate from the firm’s own funds. The interest funds legal-aid programs. Mixing it with business money is a serious ethics violation.',
    technical:
      'Governed by your state bar’s trust-accounting rules · client funds are never commingled with operating funds · subject to periodic reconciliation and audit.',
    caseSensitive: true,
  },
  {
    key: 'finra',
    term: 'FINRA',
    aliases: ['Financial Industry Regulatory Authority'],
    expansion: 'Financial Industry Regulatory Authority',
    plainEnglish:
      'The body that oversees U.S. brokers and brokerage firms — licensing reps, writing rules, and running exams like the Series 7. If you sell securities, FINRA registration and continuing education apply to you.',
    technical:
      'Self-regulatory organization overseen by the SEC · administers broker registration (Form U4), qualification exams, and the regulatory-element continuing education.',
    caseSensitive: true,
  },
  {
    key: 'sec',
    term: 'SEC',
    aliases: ['Securities and Exchange Commission'],
    expansion: 'U.S. Securities and Exchange Commission',
    plainEnglish:
      'The federal agency that regulates investments, public companies, and investment advisers. If you manage client money as a registered investment adviser, you (or your state) answer to the SEC and file Form ADV.',
    technical:
      'Federal securities regulator · investment advisers register with the SEC (≈$100M+ assets) or their state (below that) via Form ADV · enforces the Advisers Act fiduciary duty.',
    url: 'https://www.sec.gov',
    caseSensitive: true,
  },
  {
    key: 'form-adv',
    term: 'Form ADV',
    plainEnglish:
      'The registration and disclosure form every investment adviser files with the SEC or their state. Part 2 (the “brochure”) is the plain-English document you must give clients explaining your services, fees, and conflicts.',
    technical:
      'Filed via the IARD system · Part 1 = regulator data, Part 2 = client brochure · updated annually within 90 days of fiscal year-end and promptly on any material change.',
  },
  {
    key: 'smllc',
    term: 'SMLLC',
    aliases: ['single-member LLC', 'single member LLC'],
    expansion: 'Single-Member LLC',
    plainEnglish:
      'An LLC with just one owner. By default the IRS ignores it for taxes (a “disregarded entity”) and its income flows onto your personal Schedule C — unless you elect S-corp or C-corp treatment.',
    technical:
      'Treas. Reg. §301.7701-3 (check-the-box) · default = disregarded → Schedule C / E / F · can elect S-corp (Form 2553) or C-corp (Form 8832).',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/single-member-limited-liability-companies',
    caseSensitive: true,
  },
  {
    key: 'mmllc',
    term: 'MMLLC',
    expansion: 'Multi-Member LLC',
    plainEnglish:
      'An LLC with two or more owners. By default the IRS taxes it as a partnership — it files its own return (Form 1065) and hands each owner a K-1 — unless it elects S-corp or C-corp treatment.',
    technical:
      'Treas. Reg. §301.7701-3 · default = partnership (Form 1065 + K-1s, BBA audit rules) · can elect S-corp (Form 2553) or C-corp (Form 8832).',
    url: 'https://www.irs.gov/businesses/small-businesses-self-employed/limited-liability-company-llc',
    caseSensitive: true,
  },

  // --- IRC sections surfaced by irc-codes.ts (added 2026-06-20, W2-22 finish) ---
  {
    key: 'section-280a',
    term: '§280A',
    aliases: ['Section 280A', 'home office deduction'],
    plainEnglish:
      'The home-office rules. The simplified method deducts $5 per square foot of space used regularly and exclusively for business — up to 300 sq ft, so $1,500 max — with no tracking of actual home costs.',
    technical:
      'IRC §280A · simplified method $5/sq ft, 300 sq ft cap ($1,500) · space must be used regularly AND exclusively for business.',
    ircRef: 'IRC §280A',
    url: 'https://www.law.cornell.edu/uscode/text/26/280A',
  },
  {
    key: 'section-274',
    term: '§274',
    aliases: ['Section 274', '50% meal deduction'],
    plainEnglish:
      'The rule that limits how much of certain costs you can deduct — most business meals are only 50% deductible, and it disallows most client entertainment entirely.',
    technical:
      'IRC §274 · business meals generally 50% deductible · entertainment largely nondeductible since the TCJA · substantiation required.',
    ircRef: 'IRC §274',
    url: 'https://www.irs.gov/publications/p463',
  },
  {
    key: 'section-274b',
    term: '§274(b)',
    aliases: ['Section 274(b)', 'business gift limit'],
    plainEnglish:
      'The cap on deducting business gifts — at most $25 per recipient per year, no matter how much you actually spent on the gift.',
    technical:
      'IRC §274(b)(1) · $25-per-recipient-per-year deduction limit on business gifts.',
    ircRef: 'IRC §274(b)',
    url: 'https://www.irs.gov/publications/p463',
  },
  {
    key: 'section-106',
    term: '§106',
    aliases: ['Section 106', 'employer health insurance'],
    plainEnglish:
      'Health insurance the company pays for an employee is tax-free to that employee — it never lands in W-2 wages. The exception: a >2% S-corp owner, whose premiums are taxable wages with an offsetting §162(l) deduction.',
    technical:
      'IRC §106 · employer-paid health coverage excluded from employee income · >2% S-corp shareholder excluded from tax-free treatment (§1372) — taxable W-2 wages + §162(l) above-the-line deduction.',
    ircRef: 'IRC §106',
    url: 'https://www.irs.gov/publications/p15b',
  },

  // --- Accounting & reporting (Tier 1) ---
  {
    key: 'net-income',
    term: 'net income',
    aliases: ['net profit'],
    plainEnglish:
      'What is left after subtracting every expense from revenue — the bottom line of your P&L and the starting point for your tax return.',
    technical:
      'Revenue − all expenses · the foot of the income statement · feeds the tax return and owner distributions.',
  },
  {
    key: 'cash-flow',
    term: 'cash flow',
    plainEnglish:
      'The actual money moving in and out over time — different from profit, because a profitable month can still run short on cash if invoices have not been paid yet.',
    technical:
      'Operating + investing + financing cash movement · distinct from accrual net income · reconciled on the statement of cash flows.',
  },
  {
    key: 'operating-expenses',
    term: 'operating expenses',
    aliases: ['operating expense', 'OpEx'],
    plainEnglish:
      'The day-to-day costs of running the business — rent, software, payroll, supplies — as opposed to one-time, financing, or owner-draw costs.',
    technical:
      'Recurring costs of operations · excludes cost of goods sold, interest, taxes, and capital expenditures.',
  },
  {
    key: 'gross-receipts',
    term: 'gross receipts',
    plainEnglish:
      'Your total income before subtracting any expenses — the top-line number some states and tax rules key off (for example a state gross-receipts tax, or the QBI cap test).',
    technical:
      'Total revenue before deductions · used by certain state taxes (e.g. WA B&O) and federal thresholds.',
  },
  {
    key: 'amortization',
    term: 'amortization',
    plainEnglish:
      'Spreading the cost of an intangible asset — a patent, goodwill, or startup costs — over its useful life. It is the intangible-asset cousin of depreciation.',
    technical:
      'IRC §197 · 15-year straight-line for most acquired intangibles · §195 covers startup-cost amortization.',
    ircRef: 'IRC §197',
    url: 'https://www.law.cornell.edu/uscode/text/26/197',
  },
  {
    key: 'gross-profit',
    term: 'gross profit',
    plainEnglish:
      'Revenue minus the direct cost of delivering it, before overhead — it shows the margin on your core service or product.',
    technical:
      'Revenue − cost of goods/services sold · before operating expenses, interest, and taxes.',
  },
  {
    key: 'owners-equity',
    term: 'owner’s equity',
    aliases: ['owners equity', 'shareholder equity'],
    plainEnglish:
      'What the business would be worth to you after it paid off everything it owes — total assets minus total liabilities on the balance sheet.',
    technical:
      'Assets − liabilities · the balance-sheet residual claim of the owner(s).',
  },

  // --- Individual tax mechanics (Tier 2) ---
  {
    key: 'tax-bracket',
    term: 'tax bracket',
    aliases: ['marginal bracket'],
    plainEnglish:
      'One of the income bands the IRS taxes at a rising rate. Only the dollars inside a band are taxed at that band’s rate, so a raise never lowers your overall take-home.',
    technical:
      'Marginal-rate bands (Rev. Proc. 2025-32) · only income within a band is taxed at that band’s rate.',
  },
  {
    key: 'above-the-line',
    term: 'above-the-line',
    aliases: ['above the line', 'adjustment to income'],
    plainEnglish:
      'A deduction you get before AGI — it lowers AGI itself and you keep it even if you do not itemize (e.g. self-employed health insurance, half of SE tax).',
    technical:
      'IRC §62 adjustments to income · taken whether or not you itemize · reduces AGI directly.',
    ircRef: 'IRC §62',
    url: 'https://www.law.cornell.edu/uscode/text/26/62',
  },
  {
    key: 'below-the-line',
    term: 'below-the-line',
    aliases: ['below the line'],
    plainEnglish:
      'A deduction taken after AGI — either the standard deduction or your itemized deductions. You pick one or the other, not both.',
    technical:
      'IRC §63 · standard deduction vs. itemized (Schedule A) · applied after AGI to reach taxable income.',
    ircRef: 'IRC §63',
    url: 'https://www.law.cornell.edu/uscode/text/26/63',
  },
  {
    key: 'phase-out',
    term: 'phase-out',
    aliases: ['phaseout'],
    plainEnglish:
      'The income range over which a tax break shrinks and eventually disappears — many benefits do not vanish at a cliff, they fade out across a band.',
    technical:
      'A graduated reduction of a deduction/credit over an income range (e.g. QBI, SALT, AMT exemption phase-outs).',
  },
  {
    key: 'capital-gains',
    term: 'capital gains',
    aliases: ['capital gain', 'long-term capital gains'],
    plainEnglish:
      'Profit from selling an asset (stock, property) for more than you paid. Held over a year, it is taxed at the lower long-term rates instead of ordinary-income rates.',
    technical:
      'IRC §1(h) · 0/15/20% long-term brackets (Rev. Proc. 2025-32) · short-term (≤1 yr) taxed as ordinary income.',
    ircRef: 'IRC §1(h)',
    url: 'https://www.irs.gov/taxtopics/tc409',
  },
  {
    key: 'qualified-dividends',
    term: 'qualified dividends',
    aliases: ['qualified dividend'],
    plainEnglish:
      'Dividends that meet IRS holding rules and get the lower long-term capital-gains rate instead of ordinary rates — relevant when a C-corp pays out profit.',
    technical:
      'IRC §1(h)(11) · taxed at 0/15/20% long-term rates if the holding-period test is met.',
    ircRef: 'IRC §1(h)(11)',
    url: 'https://www.irs.gov/taxtopics/tc404',
  },
  {
    key: 'ordinary-income',
    term: 'ordinary income',
    plainEnglish:
      'Income taxed at the regular bracket rates — wages, business profit, interest — as opposed to the lower-taxed capital gains and qualified dividends.',
    technical:
      'Income not eligible for preferential capital-gains rates · taxed at the §1 marginal brackets.',
  },
  {
    key: 'cash-basis',
    term: 'cash basis',
    aliases: ['cash-basis', 'cash method'],
    plainEnglish:
      'Counting income when cash actually arrives and expenses when actually paid — the simpler method most small businesses use for tax. (Contrast: accrual.)',
    technical:
      'IRC §446 · income/expense recognized on cash movement · contrast accrual accounting.',
    ircRef: 'IRC §446',
    url: 'https://www.law.cornell.edu/uscode/text/26/446',
  },

  // --- Forms & schedules referenced but previously undefined (Tier 3) ---
  {
    key: 'form-1040',
    term: 'Form 1040',
    plainEnglish:
      'Your personal federal income tax return — where business profit (via K-1 or Schedule C), wages, and everything else come together.',
    technical:
      'U.S. Individual Income Tax Return · business income flows in via Schedule C or K-1 (Schedule E).',
    url: 'https://www.irs.gov/forms-pubs/about-form-1040',
  },
  {
    key: 'form-8959',
    term: 'Form 8959',
    plainEnglish:
      'The attachment that computes the extra 0.9% Additional Medicare Tax on high wages or self-employment income.',
    technical:
      'IRC §3101(b)(2) · reconciles Additional Medicare Tax withheld vs. owed above $200K single / $250K joint / $125K MFS.',
    ircRef: 'IRC §3101(b)(2)',
    url: 'https://www.irs.gov/forms-pubs/about-form-8959',
  },
  {
    key: 'form-8960',
    term: 'Form 8960',
    plainEnglish:
      'The attachment that computes the 3.8% Net Investment Income Tax on investment income above the threshold.',
    technical:
      'IRC §1411 · 3.8% NIIT on the lesser of net investment income or the amount MAGI exceeds $200K single / $250K joint.',
    ircRef: 'IRC §1411',
    url: 'https://www.irs.gov/forms-pubs/about-form-8960',
  },
  {
    key: 'form-7004',
    term: 'Form 7004',
    plainEnglish:
      'The one-page request for an automatic extension to file a business return (1120, 1120-S, 1065). It extends the filing date, not the payment date — tax owed is still due on time.',
    technical:
      'Automatic extension of time to file business returns · does NOT extend time to pay.',
    url: 'https://www.irs.gov/forms-pubs/about-form-7004',
  },
  {
    key: 'form-8832',
    term: 'Form 8832',
    plainEnglish:
      'The “check-the-box” election that lets an LLC choose how it is taxed (e.g. as a C-corp). S-corp status uses Form 2553 instead.',
    technical:
      'Treas. Reg. §301.7701-3 entity classification election · S-corp election is Form 2553.',
    url: 'https://www.irs.gov/forms-pubs/about-form-8832',
  },
  {
    key: 'schedule-a',
    term: 'Schedule A',
    plainEnglish:
      'The personal-return schedule where you list itemized deductions (state taxes up to the SALT cap, mortgage interest, charity) instead of taking the standard deduction.',
    technical:
      'IRC §63(d) itemized deductions · used only when they exceed the standard deduction.',
    ircRef: 'IRC §63(d)',
    url: 'https://www.irs.gov/forms-pubs/about-schedule-a-form-1040',
  },
  {
    key: 'schedule-e',
    term: 'Schedule E',
    plainEnglish:
      'The personal-return schedule for income from rentals, royalties, partnerships, and S-corps — this is where your K-1 amounts land on the 1040.',
    technical:
      'Supplemental Income and Loss · pass-through K-1 income from partnerships/S-corps + rental income.',
    url: 'https://www.irs.gov/forms-pubs/about-schedule-e-form-1040',
  },

  // --- Health, fringe & misc (Tier 4) ---
  {
    key: 'ichra',
    term: 'ICHRA',
    expansion: 'Individual Coverage HRA',
    plainEnglish:
      'An employer plan that reimburses workers tax-free for an individual health policy they buy themselves — a newer alternative to a group plan, with no company-size limit.',
    technical:
      'IRC §105/§106 · reimburses individual-market premiums + medical costs · same >2% S-corp owner caveat as other HRAs.',
    ircRef: 'IRC §106',
    url: 'https://www.irs.gov/forms-pubs/about-publication-969',
    caseSensitive: true,
  },
  {
    key: 'fringe-benefit',
    term: 'fringe benefit',
    aliases: ['fringe benefits'],
    plainEnglish:
      'Non-cash compensation — health insurance, retirement, an HSA. Some are tax-free to the employee (§106 health), some are taxable; the rules differ for >2% S-corp owners.',
    technical:
      'IRC §132 · taxable unless a specific exclusion applies · >2% S-corp shareholders lose several exclusions (§1372).',
    ircRef: 'IRC §132',
    url: 'https://www.irs.gov/publications/p15b',
  },
  {
    key: 'depreciation-recapture',
    term: 'depreciation recapture',
    plainEnglish:
      'When you sell a depreciated asset, the IRS “takes back” some of those deductions at sale. For equipment (§1245) it is taxed as ordinary income; for real estate (§1250) the depreciation portion is taxed at a capped 25% rate.',
    technical:
      'IRC §1245 personal-property recapture = ordinary income · §1250 real property: straight-line MACRS has no ordinary recapture — the depreciation gain is “unrecaptured §1250 gain”, taxed at a max 25% rate.',
    ircRef: 'IRC §1245 / §1250',
    url: 'https://www.law.cornell.edu/uscode/text/26/1245',
  },
  {
    key: 'geo-tagging',
    term: 'geo-tagging',
    aliases: ['geo-tag', 'geo-tagged'],
    plainEnglish:
      'Stamping an expense with where it happened. Nexus captures location on travel expenses and mileage as automatic proof of business purpose for an IRS audit.',
    technical:
      'Location metadata on expenses/mileage · substantiation evidence for travel and away-from-home deductions.',
  },
];
